import * as React from 'react';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import type { UserIntegrationFeatureAvailabilityResponse } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthVoipResourceRepository';
import { UserIntegrationOAuthVoipResourceRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthVoipResourceRepository';
import LoaderWheel from './LoaderWheel';
import type { IndEmail } from '../../MedicareProAPI/Entities/Ind/IndEmail';
import { INTEGRATION_TYPE, INTEGRATION_OPTIONS } from '../Constants/Integrations';

export interface UserSendEmailProps {
  props: {
    userAccessToken: string;
  };
  ind: Ind;
  user: User;
  onChange: ({}) => void;
  isValid: (valid: boolean) => void;
  onIntegrationSetupClick?: (type: INTEGRATION_TYPE.EMAIL) => void;
}
/**
 * Component for finding a users connected email vendor for sending, or native if enabled
 *
 * @author Andrew Brown <abrown@medicareprocrm.com>
 */
export const UserSendEmailSelection = (props: UserSendEmailProps): React.JSX.Element => {
  const [fetchingProviders, setFetchingProviders] = React.useState<boolean>(true);
  const [selectedProvider, setSelectedProvider] =
    React.useState<UserIntegrationFeatureAvailabilityResponse>();
  const [sendingTo, setSendingTo] = React.useState<string>();
  const [availableEmails, setAvailableEmails] = React.useState<Array<IndEmail>>();
  const [selectedProviderError, setSelectedProviderError] = React.useState<boolean>(false);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.props.userAccessToken);

  const userIntegrationOAuthVoipResourceRepository =
    new UserIntegrationOAuthVoipResourceRepository();
  userIntegrationOAuthVoipResourceRepository.setAccessToken(props.props.userAccessToken);

  /**
   * Handles validating if the email is sendable
   */
  const isValidToSend = () => {
    let valid = true;

    if (!sendingTo) {
      valid = false;
    }

    props.isValid(valid);
  };

  /**
   * Handles the email vendor payload and validates if able to process a send
   */
  React.useEffect(() => {
    const selections = {
      method: 'email',
      provider: selectedProvider,
      to: sendingTo,
    };

    props.onChange(selections);

    isValidToSend();
  }, [selectedProvider, sendingTo]);

  /**
   * Handles finding users integrated email vendors
   */
  React.useEffect(() => {
    userIntegrationOAuthVoipResourceRepository
      .getUserIntegrationFeatureAvailability(props.user.getUserId(), 'email')
      .then((providers) => {
        if (providers.length > 0) {
          setSelectedProvider(providers[0]);

          const first = providers[0].needs_reconnection;
          const needsReconnection = first == '1';
          setSelectedProviderError(needsReconnection);
        }

        setFetchingProviders(false);

        let thisIndEmails = props.ind.getEmails().getItems();
        if (props.ind.getSpouse()) {
          const thisIndSpouseEmails = props.ind.getSpouse().getEmails().getItems();
          thisIndEmails = [...thisIndEmails, ...thisIndSpouseEmails];
        }

        thisIndEmails.length > 0 ? setSendingTo(thisIndEmails[0].getIndEmailAddress()) : '';

        if (thisIndEmails.length > 0) {
          setAvailableEmails(thisIndEmails);
          props.isValid(true);
        }
      });
  }, []);

  return (
    <div className="mt-2 p-3 rounded bg-grey">
      {fetchingProviders && <LoaderWheel></LoaderWheel>}

      {!fetchingProviders && (
        <>
          {selectedProvider && selectedProviderError && (
            <div className="col-12">
              <div className="alert small alert-warning" role="alert">
                Your {selectedProvider.name} account needs to be reconnected.
                <div
                  className="badge badge-pill badge-info cp ml-2"
                  style={{ fontSize: '8pt' }}
                  onClick={() => props.onIntegrationSetupClick(INTEGRATION_TYPE.EMAIL)}
                >
                  <i className="far fa-plus-circle"></i> Reconnect {selectedProvider.name}
                </div>
              </div>
            </div>
          )}
          {selectedProvider && !selectedProviderError && (
            <div>
              <img
                style={{ height: '40px' }}
                className="pt-0 mt-0"
                alt={selectedProvider['name']}
                src={
                  'https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/' +
                  selectedProvider['reference'] +
                  'icon.png'
                }
              ></img>
            </div>
          )}

          {availableEmails !== undefined && (
            <>
              <label className="label">Sending to:</label>
              <select
                className="form-control form-control-sm"
                onChange={(e) => setSendingTo(e.target.value)}
              >
                {availableEmails.map((email) => {
                  return (
                    <option selected={sendingTo === email.getIndEmailAddress() ? true : false}>
                      {email.getIndEmailAddress()}
                    </option>
                  );
                })}
              </select>
            </>
          )}

          {availableEmails === undefined && (
            <div>
              <i className="far fa-exclamation-circle text-danger mr-1"></i>No email on profile
            </div>
          )}
        </>
      )}
    </div>
  );
};
